/**
 * Inspector section: validator rules declared on the selected field,
 * with an overlay of accepted overrides from `recipe.validator_overrides`.
 *
 * Each rule renders as a chip; overridden rules are muted/struck through
 * with a lock icon and a tooltip showing rationale + accepted_by. This
 * prevents an operator from `accept_override`-ing a rule that has
 * already been overridden.
 */
import React from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

export interface ValidatorRule {
  type?: string;
  rule?: string;
  allowed_values?: unknown[];
  value?: unknown;
  pattern?: string;
  message?: string;
  [k: string]: unknown;
}

/**
 * Wire-shape compatible with `MappingResponse.validator_overrides`
 * entries (`ValidatorOverride` from `services/types.ts`). Permissive
 * about nullability since the backend pydantic model marks
 * `accepted_by`/`accepted_at` as `Optional[str]`.
 */
export interface ValidatorOverrideEntry {
  rule: string;
  rationale: string;
  accepted_by?: string | null;
  accepted_at?: string | null;
}

export interface FieldRulesProps {
  target_table: string;
  target_field: string;
  /**
   * Raw validator config for this column. The hook picks up either
   *   `validator_config[field]`  (flat keying)
   * or
   *   `validator_config['table.field']`  (namespaced keying)
   * — see `services/prompt-orchestrator/app/v2/validator_pass.py:73`.
   */
  rules: ValidatorRule[] | undefined;
  /** Overrides for this `(table, field)` from `recipe.validator_overrides`. */
  overrides: ValidatorOverrideEntry[] | undefined;
  /** True when this column participates in the table's primary key. */
  isPrimaryKey?: boolean;
}

function ruleLabel(r: ValidatorRule): string {
  const kind = r.rule ?? r.type ?? 'rule';
  if (Array.isArray(r.allowed_values)) {
    const sample = r.allowed_values.slice(0, 3).map(String).join(', ');
    const more = r.allowed_values.length > 3 ? `, +${r.allowed_values.length - 3}` : '';
    return `${kind}: ${sample}${more}`;
  }
  if (r.pattern) return `${kind}: /${r.pattern}/`;
  if (r.value !== undefined && r.value !== null) return `${kind}: ${String(r.value)}`;
  return kind;
}

function ruleKey(r: ValidatorRule): string {
  return (r.rule ?? r.type ?? 'rule').toString();
}

export const FieldRules: React.FC<FieldRulesProps> = ({
  rules,
  overrides,
  isPrimaryKey,
}) => {
  const overrideByRule = new Map<string, ValidatorOverrideEntry>();
  for (const o of overrides ?? []) {
    overrideByRule.set(o.rule, o);
  }

  const hasRules = (rules?.length ?? 0) > 0;
  if (!hasRules && !isPrimaryKey) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        No declared rules.
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
      {isPrimaryKey && (
        <Tooltip title="Column participates in the primary key">
          <Chip label="PK" size="small" color="primary" variant="filled" />
        </Tooltip>
      )}
      {(rules ?? []).map((r, idx) => {
        const k = ruleKey(r);
        const override = overrideByRule.get(k);
        const overridden = Boolean(override);
        const tooltip = overridden
          ? `Override accepted by ${override?.accepted_by ?? 'unknown'} on ${override?.accepted_at?.slice(0, 10) ?? '—'} — ${override?.rationale ?? ''}`
          : (r.message ?? '');
        return (
          <Tooltip key={`${k}:${idx}`} title={tooltip}>
            <Chip
              label={ruleLabel(r)}
              size="small"
              variant="outlined"
              icon={overridden ? <LockIcon sx={{ fontSize: 14 }} /> : undefined}
              sx={{
                fontSize: '0.7rem',
                ...(overridden
                  ? {
                      opacity: 0.55,
                      textDecoration: 'line-through',
                      borderStyle: 'dashed',
                    }
                  : {}),
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export default FieldRules;
